import { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../redux/slices/accountSlice";
import LargePopupModalHeader from "../Containers/LargePopupModalHeader";
import RectangleButton from "../Buttons/RectangleButton";
import { Colors } from "../../utils/colors";
import { MassTextsActionItem } from "../CreatorDashboard/MassTexts/Items/MassTextsActionItem";
import { Icon } from "@iconify/react";
import {
  Event,
  EventType,
  NotificationType,
  ProfilePage,
  ProfilePageSection,
} from "@markit/common.types";
import CreateFormAndTrackingLinkModal from "../CreatorDashboard/FormSubmissions/CreateFormAndTrackingLinkModal";
import { API } from "../../API";
import { useOnMount } from "../../hooks/useOnMount";
import isEqual from "lodash.isequal";
import { showNotificationBanner } from "../../utils/notificationUtils";
import { handlePluralString } from "@markit/common.utils";
import { getProductDetails } from "../../utils/productUtils";
import ConfirmDeleteModal from "../Containers/ConfirmPopups/ConfirmDeleteModal";
import SelectProductItemModal from "../CreatorDashboard/Products/SelectProductItemModal";
import { ActionItemHorizontalDivider } from "../Dividers/ActionItemHorizontalDivider";

type ProfilePageAddSectionPanelProps = {
  setIsVisible: (isVisible: boolean) => void;
  currProfilePage: ProfilePage;
  profilePages: ProfilePage[];
  setProfilePages: (profilePages: ProfilePage[]) => void;
  userEvents: Event[];
};

const ProfilePageAddSectionPanel = (props: ProfilePageAddSectionPanelProps) => {
  const {
    setIsVisible,
    currProfilePage,
    profilePages,
    setProfilePages,
    userEvents,
  } = props;
  const { accountData } = useSelector(getAccountState).account;
  const dispatch = useDispatch();
  const [sections, setSections] = useState<string[]>([]);
  const [newTrackingLinkModal, setNewTrackingLinkModal] = useState(false);
  const [selectedEventType, setSelectedEventType] = useState<
    EventType | undefined
  >(undefined);
  const [listView, setListView] = useState(0);
  const [showConfirmBackModal, setShowConfirmBackModal] = useState(false);

  useOnMount(() => {
    setSections(currProfilePage.sections);
  });

  const noCurrentSections = useMemo(
    () => currProfilePage.sections.length === 0,
    [currProfilePage.sections]
  );

  const madeChanges = useMemo(
    () => !isEqual(sections, currProfilePage.sections),
    [sections, currProfilePage.sections]
  );

  const addOnPress = useCallback(async () => {
    const updatedProfilePage: ProfilePage = {
      ...currProfilePage,
      sections: sections,
    };
    await API.profile.updateProfilePage({
      uid: accountData.uid,
      profilePage: updatedProfilePage,
    });
    const updatedProfilePages = profilePages.map((page) => {
      if (page.id === currProfilePage.id) {
        return updatedProfilePage;
      }
      return page;
    });
    setProfilePages(updatedProfilePages);
    showNotificationBanner(
      dispatch,
      noCurrentSections ? "Sections Added!" : "Sections Saved!",
      NotificationType.AFFIRMATIVE
    );
    setIsVisible(false);
  }, [
    currProfilePage,
    sections,
    accountData.uid,
    profilePages,
    setProfilePages,
    dispatch,
    noCurrentSections,
    setIsVisible,
  ]);

  const backOnPress = useCallback(() => {
    if (selectedEventType) {
      setSelectedEventType(undefined);
    } else {
      setIsVisible(false);
    }
  }, [selectedEventType, setSelectedEventType, setIsVisible]);

  const individualSectionOnPress = useCallback(
    (section: ProfilePageSection | string) => {
      if (sections.includes(section)) {
        setSections(sections.filter((s) => s !== section));
      } else {
        setSections([...sections, section]);
      }
    },
    [sections]
  );

  const renderCreateNewLink = useMemo(
    () => (
      <>
        <MassTextsActionItem
          title={"Create New Link"}
          icon={
            <Icon
              icon="mdi:link-variant-plus"
              height={29}
              style={{ color: Colors.BLACK }}
            />
          }
          onPress={() => setNewTrackingLinkModal(true)}
          alternateRightIcon={
            <Icon
              icon="ion:add-circle"
              height={22}
              style={{ color: Colors.BLACK }}
            />
          }
        />
        <ActionItemHorizontalDivider dividerMargin={7} />
      </>
    ),
    []
  );

  const sectionConfigs = useMemo(
    () => [
      {
        section: ProfilePageSection.LINKS_SECTION,
        title: "All Links Section",
        subtext: "All of your public links in one place",
        icon: "mdi:link-variant",
        iconHeight: 28,
      },
      {
        section: ProfilePageSection.FORMS_SECTION,
        title: "Sign Up Forms Section",
        subtext: "All of your public sign up forms in one place",
        icon: "ion:document-text",
        iconHeight: 28,
      },
      {
        section: ProfilePageSection.POSTS_SECTION,
        title: "Posts Section",
        subtext: "All of your public blog posts in one place",
        icon: "ion:newspaper",
        iconHeight: 28,
      },
      {
        section: ProfilePageSection.PRODUCTS_SECTION,
        title: "Products Section",
        subtext: "All of your public products in one place",
        icon: "ion:bag-handle",
        iconHeight: 26,
      },
      {
        section: ProfilePageSection.EVENTS_CALENDAR,
        title: "Events Calendar",
        subtext: "Displays a calendar for all of your public events",
        icon: "ion:calendar",
        iconHeight: 26,
      },
    ],
    []
  );

  return (
    <>
      <SelectProductItemModal
        headerComp={
          <LargePopupModalHeader
            title={
              sections.length > 0 ? (
                <div
                  style={{
                    padding: "9px 14px",
                    borderRadius: 6,
                    backgroundColor: Colors.WHITE1,
                  }}
                >
                  <span className="bodySmallMedium">
                    {sections.length}{" "}
                    {handlePluralString("Section", sections.length)} Selected
                  </span>
                </div>
              ) : selectedEventType ? (
                getProductDetails(selectedEventType).title
              ) : (
                "Add Sections"
              )
            }
            altHeaderLeft={selectedEventType ? "ion:chevron-back" : undefined}
            backOnPress={() => {
              if (madeChanges && !selectedEventType) {
                setShowConfirmBackModal(true);
                return;
              }
              backOnPress();
            }}
            headerRight={
              selectedEventType ? (
                <div style={{ height: 36 }} />
              ) : (
                <RectangleButton
                  buttonLabel={noCurrentSections ? "Add" : "Save"}
                  onPress={addOnPress}
                  altColor={Colors.BLACK}
                  altTextColor={Colors.WHITE}
                  disabled={!madeChanges}
                  containerStyles={{
                    borderRadius: 100,
                    padding: "10px 14px",
                    height: 36,
                  }}
                />
              )
            }
          />
        }
        generalTabComp={
          <div className="ColumnNormal">
            {renderCreateNewLink}
            {sectionConfigs.map((config, index) => (
              <div key={config.section}>
                <MassTextsActionItem
                  title={config.title}
                  subtext={config.subtext}
                  icon={
                    <Icon
                      icon={config.icon}
                      height={config.iconHeight}
                      style={{ color: Colors.BLACK }}
                    />
                  }
                  onPress={() => individualSectionOnPress(config.section)}
                  isCheckSelected={sections.includes(config.section)}
                  checkColor={Colors.BLACK}
                />
                {index < sectionConfigs.length - 1 ? (
                  <ActionItemHorizontalDivider dividerMargin={7} />
                ) : null}
              </div>
            ))}
          </div>
        }
        items={sections}
        setItems={individualSectionOnPress}
        selectedEventType={selectedEventType}
        setSelectedEventType={setSelectedEventType}
        userEvents={userEvents}
        listView={listView}
        setListView={setListView}
        showHiddenLabel
      />
      {newTrackingLinkModal ? (
        <CreateFormAndTrackingLinkModal
          isVisible={newTrackingLinkModal}
          setIsVisible={setNewTrackingLinkModal}
          eventType={EventType.GENERIC_LINK_NO_DATA}
          onConfirmation={(linkId: string) => {
            setSections([...sections, linkId]);
            if (listView !== 1) {
              setListView(1);
            }
          }}
          defaultPublicLink
        />
      ) : null}
      <ConfirmDeleteModal
        heading="If you go back, your changes will be discarded."
        subtext="Continue?"
        deleteButtonText="Discard Changes"
        hideModal={!showConfirmBackModal}
        setIsVisible={setShowConfirmBackModal}
        deleteOnPress={backOnPress}
      />
    </>
  );
};

export default ProfilePageAddSectionPanel;
